import { ObjectID } from 'bson';
import _ from 'lodash';
import mongoose, { Schema } from 'mongoose';
import { arrayMaxLengthValidator, arrayObjectsSorted } from '../common-validators';
import {
  bookingStatuses,
  MAX_NUMBER_OF_TAGS,
  MINUTES_IN_A_DAY,
  ModerationStatus,
  Privacy,
} from '../constants';
import { preSave } from '../hooks/service-group';
import { getTripPageTitle } from '../methods/page-title';
import instructions, { IInstruction } from './instructions';
import links, { ILinks } from './links';
import localizedSchema, { ILocalizedText } from './localized-text';
import mediaServiceGroup, {
  IMediaServiceGroup,
  mediaServiceGroupValidator,
} from './media-servicegroup';
import ratingSchema, { IRating } from './rating';
import serviceGroupCapacity, { IServiceGroupCapacity } from './service-group-capacity';
import serviceGroupParent, { IServiceGroupParent } from './service-group-parent';
import serviceLocation, { IServiceLocation } from './serviceLocation';
import serviceOrganization, { IServiceOrganization } from './serviceOrganization';
import startEndLocation from './start-end-location';
import topLocation, { ITopLocation } from './top-location';
import tripPricingElement, { ITripPricingElement } from './trip-pricing';
import tripTransport, { ITripTransport } from './trip-transport';

export interface IServiceGroup {
  _id: ObjectID;
  owner: ObjectID;
  parents: IServiceGroupParent[];
  privacy: Privacy;
  status: string;
  moderationStatus: ModerationStatus;
  totalPrice: number;
  totalPricePerDay: number;
  bookablePrice: number;
  pricing: ITripPricingElement[];
  description?: ILocalizedText;
  duration: number;
  location?: IServiceLocation;
  userStartLocation?: IServiceLocation;
  userEndLocation?: IServiceLocation;
  services: IServiceOrganization[];
  transports: ITripTransport[];
  subtitle?: ILocalizedText;
  title: ILocalizedText;
  links?: ILinks;
  capacity?: IServiceGroupCapacity;
  instructions?: IInstruction;
  media: IMediaServiceGroup[];
  notes: any;
  ratings?: IRating;
  reviewCount: number;
  tags: ObjectID[];
  bookingStatus?: string;
  paymentTransactions?: ObjectID[];
  startDate?: Date;
  adultCount?: number;
  childrenCount?: number;
  infantCount?: number;
  forkedBookingsCount: number;
  hearts: number;
  fastBookable: boolean;
  topLocations: ITopLocation[];
  otherAttributes: any;
  hidePricesFromOthers: true;
  updatedAt: Date;
  createdAt: Date;
  deletedAt?: Date | null;
  publishedAt?: Date | null;
}
export interface IServiceGroupDocument extends mongoose.Document, IServiceGroup {
  _id: ObjectID;
  pricing: mongoose.Types.Array<ITripPricingElement>;
  services: mongoose.Types.Array<IServiceOrganization>;
  media: mongoose.Types.Array<IMediaServiceGroup>;
}
const serviceGroup = new Schema(
  {
    owner: {
      type: Schema.Types.ObjectId,
      ref: 'User',
      required: true,
    },
    parseUser: {
      // till we have parse in place let's store the parse user id as well
      type: String,
    },
    parents: {
      type: [serviceGroupParent],
      default: [],
      required: true,
    },
    privacy: {
      type: String,
      required: true,
      default: Privacy.private,
      enum: Object.values(Privacy),
      validate: {
        validator(this: IServiceGroup, value: string) {
          if (value === 'public' && !(this.media && this.media.length)) {
            // don't allow changing the status of a trip to public
            // if it doesn't have a
            throw new Error("Can't make a trip public with no images");
          }
          return true;
        },
        // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35860
        message: (props: any) => props.reason.message,
      },
    },
    status: {
      type: String,
      default: 'active',
      enum: ['active', 'inactive'],
      required: true,
    },
    moderationStatus: {
      type: String,
      default: ModerationStatus.pending,
      enum: Object.values(ModerationStatus),
      required: true,
    },
    totalPrice: {
      type: Number,
      required: true,
      min: 0,
    },
    bookablePrice: {
      type: Number,
      required: true,
      min: 0,
    },
    pricing: {
      type: [tripPricingElement],
      required: true,
      default: [],
    },
    description: {
      type: localizedSchema,
      required: false,
    },
    duration: {
      type: Number,
      required: true,
      default: MINUTES_IN_A_DAY, // minutes in a day
      min: [1, 'has to be at least 1 minute'],
      get(this: IServiceGroupDocument, duration: number) {
        const trip = this;
        const maxDayOrg = _.maxBy(trip.services, org => org.day);
        let calculatedDuration = 0;
        if (maxDayOrg) calculatedDuration = maxDayOrg.day * MINUTES_IN_A_DAY;
        return Math.max(duration, calculatedDuration);
      },
    },
    location: { type: serviceLocation },
    userStartLocation: { type: startEndLocation },
    userEndLocation: { type: startEndLocation },
    services: {
      type: [serviceOrganization],
      validate: arrayObjectsSorted('day'),
    },
    transports: [tripTransport],
    subtitle: {
      type: localizedSchema,
      required: false,
    },
    title: {
      type: localizedSchema,
      required: true,
    },
    links: {
      type: links,
    },
    capacity: {
      type: serviceGroupCapacity,
      required: false,
    },
    instructions: {
      type: instructions,
    },
    media: {
      type: [mediaServiceGroup],
      validate: mediaServiceGroupValidator,
    },
    notes: {
      type: Schema.Types.Mixed,
    },
    ratings: {
      type: ratingSchema,
      default: ratingSchema,
    },
    reviewCount: {
      type: Number,
      default: 0,
      min: 0,
    },
    tags: {
      type: [
        {
          type: Schema.Types.ObjectId,
          ref: 'Tag',
          required: true,
        },
      ],
      validate: arrayMaxLengthValidator(MAX_NUMBER_OF_TAGS),
      default: [],
    },
    bookingStatus: {
      type: String,
      enum: Object.values(bookingStatuses),
    },
    paymentTransactions: {
      type: [Schema.Types.ObjectId],
      ref: 'PaymentTransaction',
      default: [],
      required: true,
    },
    startDate: {
      type: Date,
    },
    adultCount: {
      type: Number,
      // it doesn't have a default value as I'm not sure what the default value should be
      // because clearly 0 doesn't seem right, and 1 also doesn't seem right as what if
      // later point in time we decide to set 2 people as default, all the documents before that point
      // will have 1 as the default and after that time 2. Basically let's just leave it blank
    },
    childrenCount: {
      type: Number,
      default: 0,
    },
    infantCount: {
      type: Number,
      default: 0,
    },
    // the number of trips who have forked this trip and booked
    forkedBookingsCount: {
      type: Number,
      default: 0,
    },
    hearts: {
      type: Number,
      default: 0,
    },
    fastBookable: {
      type: Boolean,
      default: false,
      required: true,
    },
    topLocations: {
      type: [topLocation],
    },
    otherAttributes: {},
    hidePricesFromOthers: {
      type: Boolean,
      default: false,
    },
    deletedAt: {
      type: Date,
    },
    publishedAt: {
      type: Date,
    },
  },
  {
    timestamps: true,
    toJSON: { getters: true, virtuals: true },
    toObject: { getters: true, virtuals: true },
  },
);

serviceGroup.index({ 'location.geo': '2dsphere' });
serviceGroup.index({ deletedAt: 1 });
serviceGroup.index({ owner: 1 });
serviceGroup.virtual('totalPricePerDay').get(function(this: IServiceGroup) {
  const days = Math.ceil(this.duration / MINUTES_IN_A_DAY);
  return _.round(this.totalPrice / days, 2);
});
serviceGroup.virtual('pageTitle').get(function(this: IServiceGroup) {
  return getTripPageTitle(this);
});
serviceGroup.pre('save', preSave);

export default serviceGroup;

export const localizedFields = ['title', 'description', 'subtitle'] as const;
export type ILocalizedFields = typeof localizedFields[number];
export type IServiceGroupSingleLang = Omit<IServiceGroup, ILocalizedFields> &
  { [key in ILocalizedFields]?: string };
